import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '@services/api';
import axios from 'axios';

export interface S3Object {
  bucket: string;
  key: string;
  size?: number;
  storageClass?: string;
  lastModified?: string;
  etag?: string;
  restoreStatus?: string;
  restoreExpiry?: string;
}

export interface RestoreJob {
  jobId: string;
  bucket: string;
  key: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  message?: string;
  startedAt: string;
  completedAt?: string;
  error?: string;
}

interface S3RestoreState {
  buckets: string[];
  selectedBucket: string | null;
  objects: S3Object[];
  glacierObjects: S3Object[];
  restoreJobs: RestoreJob[];
  currentObjectInfo: S3Object | null;
  loading: boolean;
  error: string | null;
  filters: {
    prefix?: string;
    storageClass?: string;
  };
}

const initialState: S3RestoreState = {
  buckets: [],
  selectedBucket: null,
  objects: [],
  glacierObjects: [],
  restoreJobs: [],
  currentObjectInfo: null,
  loading: false,
  error: null,
  filters: {},
};

// The s3-restore API returns snake_case fields (storage_class, restore_status, ...).
// Map them onto the camelCase S3Object shape this feature renders from, tolerating
// either casing so the UI keeps working if the API contract is ever camelized.
const toS3Object = (raw: any): S3Object => ({
  bucket: raw?.bucket,
  key: raw?.key,
  size: raw?.size,
  storageClass: raw?.storage_class ?? raw?.storageClass,
  lastModified: raw?.last_modified ?? raw?.lastModified,
  etag: raw?.etag,
  restoreStatus: raw?.restore_status ?? raw?.restoreStatus,
  restoreExpiry: raw?.restore_expiry ?? raw?.restoreExpiry,
});

const toS3ObjectList = (payload: any) => ({
  ...payload,
  objects: Array.isArray(payload?.objects) ? payload.objects.map(toS3Object) : [],
});

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;

    if (typeof data === 'string' && data.trim()) {
      return data;
    }

    if (data && typeof data === 'object') {
      const message = (data as any).message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }

      const detail = (data as any).detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
    }

    return error.message || fallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

// Async thunks
export const fetchBuckets = createAsyncThunk(
  's3Restore/fetchBuckets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/v2/s3-restore/buckets');
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch buckets'));
    }
  }
);

export const fetchObjects = createAsyncThunk(
  's3Restore/fetchObjects',
  async (
    { bucket, prefix, storageClass }: { bucket: string; prefix?: string; storageClass?: string },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      if (prefix) params.append('prefix', prefix);
      if (storageClass) params.append('storage_class', storageClass);

      const response = await api.get(`/api/v2/s3-restore/objects/${bucket}?${params}`);
      return toS3ObjectList(response.data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch objects'));
    }
  }
);

export const fetchGlacierObjects = createAsyncThunk(
  's3Restore/fetchGlacierObjects',
  async ({ bucket, prefix }: { bucket: string; prefix?: string }, { rejectWithValue }) => {
    try {
      const params = prefix ? `?prefix=${prefix}` : '';
      const response = await api.get(`/api/v2/s3-restore/glacier-objects/${bucket}${params}`);
      return toS3ObjectList(response.data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch glacier objects'));
    }
  }
);

export const getObjectInfo = createAsyncThunk(
  's3Restore/getObjectInfo',
  async ({ bucket, key }: { bucket: string; key: string }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/v2/s3-restore/objects/${bucket}/${encodeURIComponent(key)}/info`);
      return toS3Object(response.data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to get object info'));
    }
  }
);

export const checkRestoreStatus = createAsyncThunk(
  's3Restore/checkRestoreStatus',
  async ({ bucket, key }: { bucket: string; key: string }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/v2/s3-restore/objects/${bucket}/${encodeURIComponent(key)}/status`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to check restore status'));
    }
  }
);

export const restoreObject = createAsyncThunk(
  's3Restore/restoreObject',
  async ({ bucket, key, days = 5, tier = 'Standard' }: { 
    bucket: string; 
    key: string; 
    days?: number; 
    tier?: 'Expedited' | 'Standard' | 'Bulk' 
  }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/v2/s3-restore/restore', {
        bucket,
        key,
        days,
        tier,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to restore object'));
    }
  }
);

export const bulkRestoreObjects = createAsyncThunk(
  's3Restore/bulkRestoreObjects',
  async ({ bucket, keys, prefix, days = 5, tier = 'Standard' }: {
    bucket: string;
    keys?: string[];
    prefix?: string;
    days?: number;
    tier?: 'Expedited' | 'Standard' | 'Bulk';
  }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/v2/s3-restore/restore/bulk', {
        bucket,
        keys,
        prefix,
        days,
        tier,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to bulk restore objects'));
    }
  }
);

export const convertToStandard = createAsyncThunk(
  's3Restore/convertToStandard',
  async ({ bucket, key }: { bucket: string; key: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/v2/s3-restore/convert-to-standard', {
        bucket,
        key,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to convert to standard storage'));
    }
  }
);

const s3RestoreSlice = createSlice({
  name: 's3Restore',
  initialState,
  reducers: {
    setSelectedBucket: (state, action: PayloadAction<string | null>) => {
      state.selectedBucket = action.payload;
    },
    setFilters: (state, action: PayloadAction<{ prefix?: string; storageClass?: string }>) => {
      state.filters = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    addRestoreJob: (state, action: PayloadAction<RestoreJob>) => {
      state.restoreJobs.push(action.payload);
    },
    updateRestoreJob: (state, action: PayloadAction<RestoreJob>) => {
      const index = state.restoreJobs.findIndex(job => job.jobId === action.payload.jobId);
      if (index !== -1) {
        state.restoreJobs[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch buckets
    builder
      .addCase(fetchBuckets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuckets.fulfilled, (state, action) => {
        state.loading = false;
        state.buckets = action.payload.buckets;
      })
      .addCase(fetchBuckets.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to fetch buckets';
      });

    // Fetch objects
    builder
      .addCase(fetchObjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchObjects.fulfilled, (state, action) => {
        state.loading = false;
        state.objects = action.payload.objects;
      })
      .addCase(fetchObjects.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to fetch objects';
      });

    // Fetch glacier objects
    builder
      .addCase(fetchGlacierObjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlacierObjects.fulfilled, (state, action) => {
        state.loading = false;
        state.glacierObjects = action.payload.objects;
      })
      .addCase(fetchGlacierObjects.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to fetch glacier objects';
      });

    // Get object info
    builder
      .addCase(getObjectInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getObjectInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.currentObjectInfo = action.payload;
      })
      .addCase(getObjectInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to get object info';
      });

    // Restore object
    builder
      .addCase(restoreObject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreObject.fulfilled, (state, action) => {
        state.loading = false;
        state.restoreJobs.push(action.payload);
      })
      .addCase(restoreObject.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to restore object';
      });

    // Bulk restore
    builder
      .addCase(bulkRestoreObjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkRestoreObjects.fulfilled, (state, action) => {
        state.loading = false;
        // Handle bulk restore response
      })
      .addCase(bulkRestoreObjects.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to bulk restore objects';
      });

    // Convert to standard
    builder
      .addCase(convertToStandard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(convertToStandard.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(convertToStandard.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to convert to standard storage';
      });
  },
});

export const {
  setSelectedBucket,
  setFilters,
  clearError,
  addRestoreJob,
  updateRestoreJob,
} = s3RestoreSlice.actions;

export default s3RestoreSlice.reducer;
